const LANGUAGE_BY_EXTENSION: Record<string, string> = {
  md: 'markdown',
  markdown: 'markdown',
  mmd: 'mermaid',
  mermaid: 'mermaid',
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sh: 'shell',
  yml: 'yaml',
}

export function splitSegments(path: string): string[] {
  return path.split(/[/\\]/).filter(Boolean)
}

/**
 * Only names that change rendering are listed: `markdown` and `mermaid` select
 * dedicated renderers, the rest select syntax highlighting where the extension
 * and language name differ.
 */
export function languageForPath(path: string): string {
  const fileName = splitSegments(path).at(-1) ?? path
  const extension = fileName.includes('.') ? (fileName.split('.').at(-1) ?? '') : ''
  const lowered = extension.toLowerCase()
  return LANGUAGE_BY_EXTENSION[lowered] ?? lowered
}

/**
 * Purely a display concern: absolute host paths are long and mostly identical,
 * so trimming the shared root keeps the list readable. It is derived from the
 * paths themselves because no transcript shape carries a workspace root.
 *
 * At least one directory is always kept, so files never collapse to bare names
 * and stay distinguishable when the same name appears in several directories.
 */
export function commonRootSegmentCount(paths: string[]): number {
  const [first, ...rest] = paths
  if (first === undefined) return 0

  const candidate = splitSegments(first).slice(0, -1)
  let shared = candidate.length
  if (shared === 0) return 0

  for (const path of rest) {
    const segments = splitSegments(path)
    let index = 0
    while (index < shared && index < segments.length - 1 && candidate[index] === segments[index]) {
      index++
    }
    shared = index
    if (shared === 0) return 0
  }

  for (const path of paths) {
    shared = Math.min(shared, splitSegments(path).length - 2)
  }
  return Math.max(shared, 0)
}

/**
 * Decode git's quoted path syntax: it escapes non-ASCII bytes in octal, so
 * `café.ts` arrives as `caf\303\251.ts` and never matches the real path.
 */
export function unquoteGitPath(quoted: string): string {
  const bytes: number[] = []
  const encoder = new TextEncoder()
  for (let index = 0; index < quoted.length; index++) {
    if (quoted[index] !== '\\') {
      // Literal characters appear when `core.quotePath=false`. Encode them as
      // UTF-8; a code unit would truncate anything above U+00FF.
      const literal = String.fromCodePoint(quoted.codePointAt(index) ?? 0)
      for (const byte of encoder.encode(literal)) bytes.push(byte)
      index += literal.length - 1
      continue
    }
    const next = quoted[++index]
    if (next === undefined) break
    const octal = /^[0-7]$/.test(next) ? quoted.slice(index, index + 3) : undefined
    if (octal !== undefined && /^[0-7]{3}$/.test(octal)) {
      bytes.push(parseInt(octal, 8))
      index += 2
      continue
    }
    const simple: Record<string, number> = {a: 7, b: 8, t: 9, n: 10, v: 11, f: 12, r: 13}
    bytes.push(simple[next] ?? next.charCodeAt(0))
  }
  try {
    return new TextDecoder('utf-8', {fatal: true}).decode(new Uint8Array(bytes))
  } catch {
    return quoted
  }
}
